import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import { PAGE_TYPES } from "@store/constants";
import { useMergeProps } from "@store/Hooks/Common";
import {
    popoverStyles,
    sideMenuLinkActiveStyles,
    sideMenuLinkStyles,
    sideMenuWrapperStyles,
} from "@store/theme/mixins";
import overrideProps from "@store/utilities/overrideProps";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

export type SideMenuLinkPresentationProps = LinkPresentationProps & {
    isCurrent?: boolean;
};

export interface SideMenuStyles {
    wrapper?: InjectableCss;
    list?: InjectableCss;
    listItem?: InjectableCss;
    link?: SideMenuLinkPresentationProps;
}

const _linkStyles = () => css<SideMenuLinkPresentationProps>`
    ${sideMenuLinkStyles()}

    ${({ isCurrent }) =>
        isCurrent &&
        css`
            ${sideMenuLinkActiveStyles()}
        `}
`;

export const sideMenuStyles = {
    wrapper: {
        css: css`
            ${popoverStyles()}
        `,
    },
    list: {
        css: css`
            ${sideMenuWrapperStyles()}
        `,
    },
    listItem: {},
    link: {
        css: css`
            ${_linkStyles()}
        `,
    },
};

const List = styled.ul`
    ${injectCss}
`;
const ListItem = styled.li`
    ${injectCss}
`;
const StyledLink = styled(Link)<SideMenuLinkPresentationProps>``;

interface OwnProps {
    pageType?: string;
    nodeId?: string;
    showRootPage?: boolean;
    showSignOut?: boolean;
    overridedStyles?: SideMenuStyles;
    extendedStyles?: SideMenuStyles;
    onClick?: React.EventHandler<React.MouseEvent>;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    rootPageLink: props.pageType
        ? getPageLinkByPageType(state, props.pageType)
        : getPageLinkByNodeId(state, props.nodeId || getCurrentPage(state).nodeId),
    currentLocation: getLocation(state),
    isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
});

const mapDispatchToProps = {
    signOut,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const SideMenu: React.FunctionComponent<Props> = ({
    showRootPage,
    showSignOut,
    overridedStyles,
    extendedStyles,
    onClick,
    rootPageLink,
    currentLocation,
    isSignedIn,
    signOut,
}) => {
    if ((!rootPageLink || !rootPageLink.children) && !showSignOut) {
        return null;
    }

    const currentPageUrl = currentLocation.pathname.toLowerCase();
    const styles = useMergeProps(overrideProps(sideMenuStyles, overridedStyles), extendedStyles);
    const excludedPageTypes: Array<string> = [
        PAGE_TYPES.orderApproval,
        PAGE_TYPES.budgetManagement,
        PAGE_TYPES.savedOrderList,
        PAGE_TYPES.addresses,
        PAGE_TYPES.invoiceHistory,
        PAGE_TYPES.vmiLocationDetails,
        PAGE_TYPES.vmiLocations,
        PAGE_TYPES.vmiUsers,
    ];

    const _links = rootPageLink?.children?.filter(
        x => !x.excludeFromNavigation && !excludedPageTypes.some(y => x.type === y),
    );

    return (
        <StyledWrapper {...styles.wrapper}>
            <List {...styles.list}>
                {showRootPage && rootPageLink && (
                    <ListItem {...styles.listItem}>
                        <StyledLink
                            href={rootPageLink.url}
                            isCurrent={currentPageUrl === rootPageLink.url.toLowerCase()}
                            onClick={onClick}
                            {...styles.link}
                        >
                            {rootPageLink.title}
                        </StyledLink>
                    </ListItem>
                )}

                {_links?.map(link => (
                    <ListItem key={link.id} {...styles.listItem}>
                        <StyledLink
                            href={link.url}
                            isCurrent={currentPageUrl.startsWith(link.url.toLowerCase())}
                            onClick={onClick}
                            {...styles.link}
                        >
                            {link.title}
                        </StyledLink>
                    </ListItem>
                ))}

                {isSignedIn && showSignOut && (
                    <ListItem {...styles.listItem}>
                        <StyledLink
                            onClick={(event: React.MouseEvent) => {
                                signOut();
                                onClick?.(event);
                            }}
                            {...styles.link}
                        >
                            {translate("Sign Out")}
                        </StyledLink>
                    </ListItem>
                )}
            </List>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SideMenu);
